import { Component, computed, DestroyRef, inject, linkedSignal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SharedataService } from '@services/sharedata.service';
import { environment } from '@env/environment';
import { ThemeService } from '@services/theme.service';
import { ConfigurationService } from '@services/configuration.service';
import { catchError, EMPTY } from 'rxjs';
import { DomSanitizer } from '@angular/platform-browser';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_ERROR_HANDLER } from './_core/error.interceptor';
import { WINDOW } from '@tokens/window.token';
import { nebulaConst } from '@interfaces/nebulaConst';
import { RouterOutlet } from '@angular/router';
import { ClientNotificationConnectionService } from '@services/client-notifications/client-notification-connection.service';
import { DebugInfoComponent } from '@components/debug-info/debug-info.component';
import { LoadingPanelComponent } from '@components/loading-panel/loading-panel.component';
import { GlobalNotificationComponent } from '@components/notify/global-notification/global-notification.component';
import { VersionCheckService } from '@services/version-check.service';
import { ServerReconnectService } from '@services/server-reconnect.service';
import { UserContextLoaderService } from '@services/user-context-loader.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [DebugInfoComponent, RouterOutlet, LoadingPanelComponent, GlobalNotificationComponent]
})
export class AppComponent implements OnInit {
  private configurationService = inject(ConfigurationService);
  private sharedData = inject(SharedataService);
  private notificationConnection = inject(ClientNotificationConnectionService);
  private themeService = inject(ThemeService);
  private _window = inject<Window>(WINDOW);
  private httpClient = inject(HttpClient);
  private sanitizer = inject(DomSanitizer);
  private versionCheck = inject(VersionCheckService);
  private serverReconnect = inject(ServerReconnectService);
  private userContextLoader = inject(UserContextLoaderService);
  private destroyRef = inject(DestroyRef);

  protected readonly displayedBackground = this.userContextLoader.displayedBackground;

  // Farbmodus des angezeigten Hintergrunds; unbekannter Hintergrund behält den bisherigen Modus, anfangs hell
  private readonly colorMode = linkedSignal<string, 'light' | 'dark'>({
    source: this.displayedBackground,
    computation: (bg, previous) =>
      this.themeService.getAllBackgrounds()?.find(b => b.backgroundSrc === bg)?.colorMode ?? previous?.value ?? 'light'
  });

  protected readonly modeClass = computed(
    () => (environment.testing ? 'development-mode ' : '') + `${this.colorMode()}-mode`
  );

  constructor() {
    this.serverReconnect.start();

    // Start: App-Titel und SVG-Logo einmalig laden
    this.configurationService.getAppTitle().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    this.loadAppLogo();
    this._window.document.documentElement.lang = localStorage.getItem('language') || 'de';

    this.userContextLoader.start();

    this.destroyRef.onDestroy(() => this.notificationConnection.onDestroy());
  }

  ngOnInit(): void {
    this.versionCheck.start();
  }

  /**
   * SVG-Logo laden; fehlt es, bleibt das Signal null und es wird das PNG-Logo angezeigt.
   * Fehler werden am Error-Interceptor vorbeigeleitet, damit keine Fehlermeldung erscheint.
   */
  private loadAppLogo(): void {
    this.httpClient
      .get(nebulaConst.appLogoSvg, {
        responseType: 'text',
        context: new HttpContext().set(SKIP_ERROR_HANDLER, true)
      })
      .pipe(
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(value => {
        if (value) {
          this.sharedData.appLogoSvg.set(this.sanitizer.bypassSecurityTrustHtml(value));
        }
      });
  }
}
